import { requestJson } from "@/shared/services/api";
import type { PublicSiteSettings } from "../context/PublicServerDataContext";
import type {
  PublishedArticleCard,
  PublishedArticleDetail,
  PublishedCaseCard,
  PublishedCaseDetail,
} from "../types/content.types";
import type { Locale } from "@/shared/i18n/locale";
import {
  getPublishedArticles as fetchPublishedArticles,
  getPublishedArticleBySlug as fetchPublishedArticleBySlug,
  type PublicArticleCardResponse,
  type PublicArticleDetailResponse,
} from "./articlesApi.service";
import {
  getPublishedCases as fetchPublishedCases,
  getPublishedCaseBySlug as fetchPublishedCaseBySlug,
  type PublicCaseCardResponse,
  type PublicCaseDetailResponse,
  type PublicCaseMetricResponse,
} from "./casesApi.service";

export type {
  PublicArticleCardResponse,
  PublicArticleDetailResponse,
  PublicCaseCardResponse,
  PublicCaseDetailResponse,
  PublicCaseMetricResponse,
};

interface ArticleLikeResponse {
  liked: boolean;
  likes_count: number;
}

export interface ArticleLikeState {
  liked: boolean;
  likesCount: number;
}

interface PublicSiteSettingsResponse {
  articles_enabled: boolean;
}

export async function getPublicSiteSettings(
  signal?: AbortSignal,
): Promise<PublicSiteSettings> {
  const settings = await requestJson<PublicSiteSettingsResponse>(
    "/api/public/site-settings",
    { signal },
  );
  return { articlesEnabled: settings.articles_enabled };
}

export async function getPublishedArticles(
  localeOrSignal?: Locale | AbortSignal,
  signal?: AbortSignal,
): Promise<PublishedArticleCard[]> {
  const locale = typeof localeOrSignal === "string" ? localeOrSignal : undefined;
  const actualSignal = localeOrSignal instanceof AbortSignal ? localeOrSignal : signal;
  return fetchPublishedArticles(locale, actualSignal);
}

export async function getPublishedArticleBySlug(
  slug: string,
  signal?: AbortSignal,
  locale?: Locale,
): Promise<PublishedArticleDetail> {
  return fetchPublishedArticleBySlug(slug, signal, locale);
}

export async function getArticleLikeState(slug: string): Promise<ArticleLikeState> {
  return toArticleLikeState(
    await requestJson<ArticleLikeResponse>(`/api/public/articles/${slug}/likes`, {
      credentials: "include",
    }),
  );
}

export async function toggleArticleLike(slug: string): Promise<ArticleLikeState> {
  return toArticleLikeState(
    await requestJson<ArticleLikeResponse>(`/api/public/articles/${slug}/likes/toggle`, {
      method: "POST",
      credentials: "include",
    }),
  );
}

export async function getPublishedCases(
  localeOrSignal?: Locale | AbortSignal,
  signal?: AbortSignal,
): Promise<PublishedCaseCard[]> {
  const locale = typeof localeOrSignal === "string" ? localeOrSignal : undefined;
  const actualSignal = localeOrSignal instanceof AbortSignal ? localeOrSignal : signal;
  return fetchPublishedCases(locale, actualSignal);
}

export async function getPublishedCaseBySlug(
  slug: string,
  signal?: AbortSignal,
  locale?: Locale,
): Promise<PublishedCaseDetail> {
  return fetchPublishedCaseBySlug(slug, signal, locale);
}

function toArticleLikeState(response: ArticleLikeResponse): ArticleLikeState {
  return { liked: response.liked, likesCount: response.likes_count };
}
